import { useMutation } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  ApiException,
  ExternalPaymentMethodType,
  Feature,
  SaleLineItemCoverage,
} from 'src/WebApiController';

export type ApplyExternalPaymentInput = {
  externalPaymentMethodType: ExternalPaymentMethodType;
  externalPaymentNote?: string | null;
  customPaymentTypeName?: string | null;
  externalPaymentAmount?: number | null;
  externalPaymentDate?: string | null;
  coverages?: SaleLineItemCoverage[] | null;
};

/**
 * Discriminated on which entity the external payment targets:
 *  - group variant: `{ salesGroupId }` → `?salesGroupId=` (multi-sale group, unchanged)
 *  - sale variant:  `{ saleId }`       → `?saleId=` (TRUE single retail sale, POS-9097)
 * Exactly one of the two is set; the backend resolves the target from whichever
 * query param is present.
 */
export type ApplyExternalPaymentRequest =
  | { salesGroupId: string; saleId?: never; input: ApplyExternalPaymentInput }
  | { saleId: number; salesGroupId?: never; input: ApplyExternalPaymentInput };

export type ApplyExternalPaymentResponse = {
  /** Set for the group path; null for the single-sale path (POS-9097). */
  salesGroupId: string | null;
  /** Set for the single-sale path; null for the group path (POS-9097). */
  saleId: number | null;
  marketplacePaymentState: number;
  pointOfSalePaymentState: number;
};

/**
 * Mutation hook to call POST /api/Sale/ApplyExternalPayment.
 * Used in Step 2 of the partial external payment flow: after an Unpaid sale is
 * created via CreateRetailSaleGroup (without external fields), the broker
 * selects coverages and this hook applies the external payment to the sale,
 * transitioning it to Paid or PartiallyPaid.
 *
 * Accepts EITHER a group target (`{ salesGroupId }` → `?salesGroupId=`) or a single
 * sale target (`{ saleId }` → `?saleId=`, a TRUE single retail sale, POS-9097).
 *
 * Gated behind Feature.RetailSale to control access to external payment functionality.
 *
 * NOTE: This endpoint is not yet in the auto-generated SaleClient (pending Services
 * 29.20.1 publish). The hook uses a raw fetch call via transformOptions from
 * PosClientBase. Remove this hook and replace with SaleClient.applyExternalPayment
 * once the generated types are updated.
 */
export function useApplyExternalPayment() {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError, showErrorDialog } = useClientLogContext();
  // Feature flag: gated behind RetailSale to control access to external payment functionality
  const hasRetailSaleFeature = useUserHasFeature(Feature.RetailSale);

  return useMutation({
    mutationFn: async (
      request: ApplyExternalPaymentRequest
    ): Promise<ApplyExternalPaymentResponse> => {
      if (!hasRetailSaleFeature) {
        throw new Error('RetailSale feature is not enabled');
      }

      const { input } = request;
      // Single-sale variant targets ?saleId=; group variant keeps ?salesGroupId=.
      const url =
        'saleId' in request && request.saleId != null
          ? `/api/Sale/ApplyExternalPayment?saleId=${encodeURIComponent(request.saleId)}`
          : `/api/Sale/ApplyExternalPayment?salesGroupId=${encodeURIComponent((request as { salesGroupId: string }).salesGroupId)}`;

      const init: RequestInit = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': navigator.language,
          ActiveAccountId: activeAccountWebClientConfig.activeAccountId ?? '',
        },
        body: JSON.stringify(input),
      };

      const response = await fetch(url, init);
      if (!response.ok) {
        const responseText = await response
          .text()
          .catch(() => response.statusText);
        throw new ApiException(
          `ApplyExternalPayment failed: ${response.status}`,
          response.status,
          responseText,
          {},
          null
        );
      }

      return response.json() as Promise<ApplyExternalPaymentResponse>;
    },
    onError: (error: ErrorTypes) => {
      trackError('SaleClient.applyExternalPayment', error);
      showErrorDialog('SaleClient.applyExternalPayment', error);
    },
  });
}
